/**
 * Step 8: AI Validation - KI prüft die berechneten Werte
 */
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, Sparkles, Loader2, Shield, Lightbulb, Star, ThumbsUp, XCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StepAIValidation = ({ prefs, calculatedGoals, token, isAdmin, customerId, onValidationComplete }) => {
  const [validating, setValidating] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    validateWithAI();
  }, []);

  const validateWithAI = async () => {
    setValidating(true);
    setError(null);
    
    try {
      // Use different endpoint for admin vs customer
      const endpoint = isAdmin && customerId
        ? `${API}/nutrition/customer/${customerId}/validate-goals`
        : `${API}/portal/nutrition/validate-nutrition-goals`;
      
      const response = await axios.post(
        endpoint,
        {
          height_cm: prefs.height_cm,
          weight_kg: prefs.weight_kg,
          age: prefs.age,
          gender: prefs.gender,
          activity_level: prefs.activity_level,
          work_activity: prefs.work_activity_level,
          cycle_type: prefs.cycle_type,
          bmr: calculatedGoals?.bmr || 0,
          // Use tdee_with_work to include work activity bonus
          tdee: calculatedGoals?.tdee_with_work || calculatedGoals?.tdee || 0,
          work_bonus: calculatedGoals?.work_bonus || 0,
          daily_calories: calculatedGoals?.daily_calories || 0,
          protein_grams: calculatedGoals?.protein_grams || 0,
          carbs_grams: calculatedGoals?.carbs_grams || 0,
          fat_grams: calculatedGoals?.fat_grams || 0,
          protein_percent: calculatedGoals?.protein_percent || 25,
          carbs_percent: calculatedGoals?.carbs_percent || 50,
          fat_percent: calculatedGoals?.fat_percent || 25,
          body_fat_percent: prefs.body_fat_percent,
          body_comp_adjustment: calculatedGoals?.body_comp_adjustment || 0,
          sports_calories_daily: calculatedGoals?.sports_calories_daily || 0,
          daily_routine: prefs.daily_routine || null,
          sports_activities: prefs.sports_activities || []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResult(response.data);
      if (onValidationComplete) {
        onValidationComplete(response.data);
      }
    } catch (err) {
      console.error('Validation error:', err);
      setError('Validierung fehlgeschlagen');
      // Still allow to continue on error
      setResult({ valid: true, score: 0, message: 'Validierung übersprungen' });
    } finally {
      setValidating(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-400';
    if (score >= 6) return 'text-yellow-400';
    if (score >= 4) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreIcon = (score) => {
    if (score >= 9) return Star;
    if (score >= 8) return Sparkles;
    if (score >= 6) return ThumbsUp;
    if (score >= 4) return AlertTriangle;
    return XCircle;
  };

  return (
    <div className="space-y-4" data-testid="onboarding-step-ai-validation">
      <div className="text-center mb-6">
        <Shield className="w-12 h-12 text-purple-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-white">KI-Validierung</h3>
        <p className="text-zinc-400 text-sm">Die KI überprüft deine berechneten Werte</p>
      </div>

      {validating ? (
        <div className="py-8 text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-purple-500 mb-4" />
          <p className="text-zinc-300">KI analysiert deine Daten...</p>
          <p className="text-zinc-500 text-sm mt-2">Dies dauert nur wenige Sekunden</p>
        </div>
      ) : result ? (
        <div className="space-y-4">
          {/* Score Card */}
          <div className={`p-4 rounded-xl border-2 ${
            result.valid 
              ? 'bg-green-500/10 border-green-500/50' 
              : 'bg-orange-500/10 border-orange-500/50'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.valid ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-orange-500" />
                )}
                <span className="font-semibold text-white">
                  {result.valid ? 'Werte plausibel' : 'Hinweise beachten'}
                </span>
              </div>
              {result.score > 0 && (
                <div className={`text-2xl font-bold flex items-center gap-1.5 ${getScoreColor(result.score)}`}>
                  {(() => { const Icon = getScoreIcon(result.score); return <Icon className="w-6 h-6" />; })()} {result.score}/10
                </div>
              )}
            </div>
            
            {result.message && (
              <p className="text-zinc-300 text-sm">{result.message}</p>
            )}
          </div>

          {/* Warnings */}
          {result.warnings && result.warnings.length > 0 && (
            <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                <span className="text-orange-400 font-medium text-sm">Hinweise</span>
              </div>
              <ul className="space-y-1">
                {result.warnings.map((warning, idx) => (
                  <li key={idx} className="text-zinc-300 text-sm flex items-start gap-2">
                    <span className="text-orange-400">•</span>
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tips */}
          {result.tips && result.tips.length > 0 && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 font-medium text-sm">Tipps</span>
              </div>
              <ul className="space-y-1">
                {result.tips.map((tip, idx) => (
                  <li key={idx} className="text-zinc-300 text-sm flex items-start gap-2">
                    <Lightbulb className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Summary of calculated values */}
          <div className="p-3 bg-zinc-800/50 rounded-lg">
            <p className="text-zinc-400 text-xs mb-2">Berechnete Werte:</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">BMR:</span>
                <span className="text-white">{calculatedGoals?.bmr || '-'} kcal</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">TDEE:</span>
                <span className="text-white">{calculatedGoals?.tdee || '-'} kcal</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Tagesziel:</span>
                <span className="text-yellow-400 font-bold">{calculatedGoals?.daily_calories || '-'} kcal</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Makros:</span>
                <span className="text-white">
                  {calculatedGoals?.protein_percent || 25}/{calculatedGoals?.carbs_percent || 50}/{calculatedGoals?.fat_percent || 25}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
          <p className="text-red-400">{error}</p>
          <button
            onClick={validateWithAI}
            className="mt-2 text-sm text-zinc-400 hover:text-white"
          >
            Erneut versuchen
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default StepAIValidation;
